import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Logo } from "../brand/Logo";
import { BrandButton } from "../brand/BrandButton";
import { LoginButton } from "../../features/auth";
import { useAdminGuard } from "@/features/admin";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { Menu, X } from "lucide-react";
import { useState, useCallback } from "react";
import { cn } from "@/lib/utils";

/**
 * Header — top navigation bar.
 *
 * Polished from the previous blue-gradient bar:
 *  - Background: white with bottom hairline + sticky backdrop blur.
 *  - Nav links: text-only (lucide icons removed; iconography in a top
 *    nav competes with the brand wordmark and was visually noisy).
 *  - Active state: text-dark + font-semibold; no pill background.
 *  - Logo: renders on light (default Logo variant; the `onDark` flag
 *    was for the old blue background and is no longer set).
 *
 * Mobile hamburger menu kept; restyled for the white surface.
 *
 * LoginButton and LanguageSwitcher are left untouched here — their
 * own internal styling already works on light surfaces.
 */
export function Header() {
  const location = useLocation();
  const { t } = useTranslation();
  const { navVisible: showAdmin } = useAdminGuard();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string): boolean => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const navItems = [
    { path: "/", label: t("nav.markets") },
    { path: "/world-cup", label: t("nav.worldCup") },
    { path: "/leaderboard", label: t("nav.leaderboard") },
    { path: "/profile", label: t("nav.profile") },
    // Admin entry: only when the connected wallet holds an operator/admin
    // KV role (useAdminGuard). Replaces the old hidden-URL access.
    ...(showAdmin ? [{ path: "/admin", label: t("nav.admin") }] : []),
  ];

  return (
    <header className="bg-white/95 backdrop-blur border-b border-border-card sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="group flex-shrink-0"
            onClick={closeMobileMenu}
          >
            <Logo size="md" withText />
          </Link>

          {/* Desktop nav links — text only */}
          <nav className="hidden md:flex items-center gap-1 ml-10">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "px-3 py-1.5 text-sm rounded-md transition-colors",
                    active
                      ? "text-text-dark font-semibold"
                      : "text-text-light font-medium hover:text-text-dark hover:bg-gray-50",
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Create Market — quiet text link */}
            <Link
              to="/create"
              className="hidden md:inline-flex px-3 py-1.5 text-sm font-medium text-text-light hover:text-text-dark transition-colors"
            >
              + {t("nav.createMarket")}
            </Link>

            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>

            <div className="hidden sm:block">
              <LoginButton size="default" />
            </div>

            {/* Mobile menu toggle */}
            <BrandButton
              variant="ghost"
              size="icon"
              className="md:hidden text-text-light hover:text-text-dark hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? t("common.close") : "Menu"}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </BrandButton>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileMenuOpen && (
          <nav className="md:hidden border-t border-border-card pt-3 pb-1 mt-3 space-y-1 animate-fade-in-down">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={cn(
                    "block px-4 py-2.5 rounded-lg transition-all text-sm",
                    active
                      ? "bg-gray-100 text-text-dark font-semibold"
                      : "text-text-light hover:bg-gray-50 hover:text-text-dark",
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              to="/create"
              onClick={closeMobileMenu}
              className="block px-4 py-2.5 text-sm text-text-light hover:bg-gray-50 hover:text-text-dark rounded-lg transition-colors"
            >
              + {t("nav.createMarket")}
            </Link>
            <div className="border-t border-border-card pt-2 mt-2 space-y-2">
              <div className="px-2">
                <LanguageSwitcher />
              </div>
              <div className="px-2">
                <LoginButton size="default" className="w-full" />
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
